/**
 * Utility functions to safely access object fields that may be in camelCase or snake_case
 * This handles inconsistencies between API responses and frontend code
 * 
 * Pattern from commit: 20e52eff9828d8fb08a46a0ac0b92e8c8f36744c
 */

/**
 * Get firstName from object (handles both camelCase and snake_case)
 * @param {Object} obj - Object to get firstName from
 * @param {string} fallback - Fallback value if not found
 * @returns {string}
 */
export const getFirstName = (obj, fallback = '') => {
  if (!obj) {return fallback;}
  return obj.firstName || obj.first_name || fallback;
};

/**
 * Get lastName from object (handles both camelCase and snake_case)
 * @param {Object} obj - Object to get lastName from
 * @param {string} fallback - Fallback value if not found
 * @returns {string}
 */
export const getLastName = (obj, fallback = '') => {
  if (!obj) {return fallback;}
  return obj.lastName || obj.last_name || fallback;
};

/**
 * Get full name from object (handles both camelCase and snake_case)
 * @param {Object} obj - Object to get name from
 * @param {string} fallback - Fallback value if not found
 * @returns {string}
 */
export const getFullName = (obj, fallback = 'Unknown') => {
  if (!obj) {return fallback;}
  const firstName = getFirstName(obj);
  const lastName = getLastName(obj);
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || obj.name || fallback;
};

/**
 * Get any field that might be in camelCase or snake_case
 * @param {Object} obj - Object to get field from
 * @param {string} camelCaseKey - camelCase key name
 * @param {string} snakeCaseKey - snake_case key name
 * @param {any} fallback - Fallback value if not found
 * @returns {any}
 */
export const getField = (obj, camelCaseKey, snakeCaseKey, fallback = '') => {
  if (!obj) {return fallback;}
  return obj[camelCaseKey] || obj[snakeCaseKey] || fallback;
};

